import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ScheduleIcon from "@mui/icons-material/Schedule";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RefreshIcon from "@mui/icons-material/Refresh";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import _request from "actions/_request";
import {
  summarizePublishTasks,
  describeTaskFailure,
  findTokenFailures,
  describeThread,
  describeResumeError,
  THREADS_RECONNECT_CODES,
} from "utils/publishStatus";

const POLL_INTERVAL_MS = 4000;
// Hard cap so we never poll forever if the backend never terminates a task.
const MAX_POLL_DURATION_MS = 15 * 60 * 1000;

const isTerminal = (status) => status === "success" || status === "fail";

function StatusIcon({ task }) {
  if (!isTerminal(task.status) || (task.social_type === "tiktok" && task.result?.pending)) {
    if (task.status === "queued") {
      return <ScheduleIcon sx={{ fontSize: 18, color: "#9e9e9e" }} />;
    }
    return <CircularProgress size={16} sx={{ color: "#1976d2" }} />;
  }
  if (task.status === "fail" || task.result?.success === false) {
    return <ErrorIcon sx={{ fontSize: 18, color: "#d32f2f" }} />;
  }
  if (task.result?.status?.status === "SEND_TO_USER_INBOX") {
    return <InboxIcon sx={{ fontSize: 18, color: "#ed6c02" }} />;
  }
  return <CheckCircleIcon sx={{ fontSize: 18, color: "#2e7d32" }} />;
}

function statusLabel(task, tr) {
  const thread = describeThread(task);
  if (task.status === "queued") return tr ? tr("publishStatus.queued") : "Queued";
  if (task.status === "processing") {
    // A chain reports each part as it goes out (`result.in_progress`).
    if (thread && tr) return tr("publishStatus.threadProgress", { published: thread.published, total: thread.total });
    return tr ? tr("publishStatus.processing") : "Publishing…";
  }
  if (task.social_type === "tiktok" && task.result?.pending) {
    return tr ? tr("publishStatus.tiktokRepolling") : "TikTok is still processing…";
  }
  if (task.status === "fail" || task.result?.success === false) {
    // Prefer the server's sentence over its error code — the code alone
    // ("LI_TOKEN_INVALID") means nothing to the person reading it.
    const { detail, code } = describeTaskFailure(task);

    // An expired session is the one failure the user can fix themselves, so it gets
    // translated copy rather than the server's English sentence (OP#2449).
    if (code === "SESSION_EXPIRED" && tr) {
      const provider = task.social_type
        ? task.social_type.charAt(0).toUpperCase() + task.social_type.slice(1)
        : "";
      return tr("errors.session_expired", { provider });
    }

    // A chain: say how far it got; the parts list below says which part failed and why.
    if (thread && tr) {
      return tr("publishStatus.threadProgress", { published: thread.published, total: thread.total });
    }

    return detail || code || (tr ? tr("publishStatus.failed") : "Failed");
  }
  if (task.result?.status?.status === "SEND_TO_USER_INBOX") {
    return tr
      ? tr("publishStatus.tiktokInbox")
      : "Sent to TikTok drafts — open the app to publish";
  }
  if (thread && tr) {
    return tr("publishStatus.threadProgress", { published: thread.published, total: thread.total });
  }
  return tr ? tr("publishStatus.published") : "Published";
}

function ThreadPartIcon({ status }) {
  if (status === "published") return <CheckCircleIcon sx={{ fontSize: 16, color: "#2e7d32" }} />;
  if (status === "failed") return <ErrorIcon sx={{ fontSize: 16, color: "#d32f2f" }} />;
  if (status === "unknown") return <HelpOutlineIcon sx={{ fontSize: 16, color: "#ed6c02" }} />;
  return <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />;
}

// The parts of a Threads chain (OP#3466 `result.thread.parts`): status, a link to each live part, and why a
// part did not go out.
export function ThreadParts({ thread, tr }) {
  return (
    <Box data-testid="thread-parts" sx={{ mt: "8px", display: "flex", flexDirection: "column", gap: "4px" }}>
      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "var(--text-color)", opacity: 0.7 }}>
        {tr("publishStatus.threadPartsTitle")}
      </Typography>
      {thread.parts.map((part) => {
        const reason = part.status === "unknown" ? tr("publishStatus.threadPartUnknown") : part.error || part.errorCode;
        return (
          <Box key={part.position} data-testid="thread-part" sx={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "12px" }}>
            <Box sx={{ mt: "1px" }}>
              <ThreadPartIcon status={part.status} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, color: "var(--text-color)" }}>
              <span style={{ fontWeight: 600 }}>
                {tr("publishStatus.threadPart", { n: part.position, total: thread.total })}
              </span>
              {" · "}
              <span style={{ opacity: 0.75 }}>{tr(`publishStatus.threadPartStatus.${part.status}`)}</span>
              {(part.status === "failed" || part.status === "unknown") && reason && (
                <Box sx={{ color: "#d32f2f", overflowWrap: "anywhere" }}>
                  {tr("publishStatus.threadPartFailed", { reason })}
                </Box>
              )}
            </Box>
            {part.permalink && (
              <Button
                size="small"
                component="a"
                href={part.permalink}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
                aria-label={`${tr("publishStatus.threadOpen")} (${tr("publishStatus.threadPart", { n: part.position, total: thread.total })})`}
                sx={{ fontSize: "11px", textTransform: "none", minWidth: 0, padding: "0 6px" }}>
                {tr("publishStatus.threadOpen")}
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function TaskRow({ task, account, tr, onReconnect, onResume, resume }) {
  const [expanded, setExpanded] = React.useState(false);
  const failed = task.status === "fail" || task.result?.success === false;
  const thread = tr ? describeThread(task) : null;
  const hasDetails = failed || task.result?.status?.status === "SEND_TO_USER_INBOX" || Boolean(thread);
  const {
    code: failureCode,
    detail: failureDetail,
    raw: failureRaw,
    isTokenFailure,
  } = failed ? describeTaskFailure(task) : {};
  // Threads: a reconnect also fixes a token that lacks the replies permission.
  const showReconnect =
    failed &&
    (isTokenFailure || (task.social_type === "threads" && THREADS_RECONNECT_CODES.includes(failureCode)));
  const resumeBusy = Boolean(resume && resume.busy);
  // A chain that stopped part-way opens on its parts, so the failed one is visible without a click.
  const threadBroken = Boolean(thread && failed && thread.failed.length > 0);
  React.useEffect(() => {
    if (threadBroken) setExpanded(true);
  }, [threadBroken]);

  return (
    <Box
      sx={{
        padding: "10px 12px",
        borderRadius: "8px",
        backgroundColor: "var(--bg-secondary, rgba(0,0,0,0.02))",
        border: "1px solid var(--border-color)",
      }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <StatusIcon task={task} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "var(--text-color)" }} noWrap>
            {account?.account_name || account?.username || `Account #${task.account_id}`}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.65 }}>
            {task.social_type} · {statusLabel(task, tr)}
          </Typography>
        </Box>
        {thread && thread.rootPermalink && (
          <Button
            size="small"
            component="a"
            href={thread.rootPermalink}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
            sx={{ fontSize: "11px", textTransform: "none", whiteSpace: "nowrap" }}>
            {tr("publishStatus.threadOpen")}
          </Button>
        )}
        {thread && thread.resumable && onResume && (
          <Button
            size="small"
            variant="contained"
            disabled={resumeBusy}
            onClick={() => onResume(task)}
            sx={{ fontSize: "11px", textTransform: "none", whiteSpace: "nowrap" }}>
            {resumeBusy ? <CircularProgress size={12} sx={{ color: "inherit", mr: "6px" }} /> : null}
            {tr("publishStatus.threadContinue")}
          </Button>
        )}
        {showReconnect && onReconnect && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={() => onReconnect(task)}
            sx={{ fontSize: "11px", textTransform: "none", whiteSpace: "nowrap" }}>
            {tr ? tr("publishStatus.reconnect") : "Reconnect"}
          </Button>
        )}
        {hasDetails && (
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ fontSize: "11px", textTransform: "none" }}>
            {expanded ? (tr ? tr("publishStatus.hideDetails") : "Hide") : (tr ? tr("publishStatus.details") : "Details")}
          </Button>
        )}
      </Box>
      {resume && resume.errorKey && (
        <Typography role="alert" sx={{ mt: "6px", fontSize: "12px", color: "#d32f2f" }}>
          {tr(resume.errorKey)}
        </Typography>
      )}
      {resume && resume.done && !isTerminal(task.status) && (
        <Typography sx={{ mt: "6px", fontSize: "12px", color: "var(--text-color)", opacity: 0.75 }}>
          {tr("publishStatus.threadContinued")}
        </Typography>
      )}
      {expanded && thread && <ThreadParts thread={thread} tr={tr} />}
      {expanded && hasDetails && (failed || !thread) && (
        <Box
          sx={{
            mt: "8px",
            padding: "8px 10px",
            backgroundColor: "rgba(0,0,0,0.04)",
            borderRadius: "6px",
            fontSize: "11px",
            color: "var(--text-color)",
            opacity: 0.8,
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}>
          {failed
            ? [failureDetail || failureCode, failureRaw, failureDetail && failureCode]
                .filter(Boolean)
                .join("\n") || "Unknown error"
            : "Your video is waiting in the TikTok drafts folder. Open the TikTok app to review and publish it."}
        </Box>
      )}
    </Box>
  );
}

// The server answers a resume with `status: queued`; showing it right away keeps the dialog from reading the old
// failure as final until the next poll.
const markTaskQueued = (tasksByPost, taskId) => {
  const requeue = (t) => (t.id === taskId ? { ...t, status: "queued" } : t);
  const next = {};
  Object.keys(tasksByPost).forEach((postId) => {
    next[postId] = (tasksByPost[postId] || []).map(requeue);
  });
  return next;
};

const PublishStatusDialog = ({
  open,
  postIds,
  accounts,
  onClose,
  tr,
  onTokenFailures,
  onReconnect,
}) => {
  const [tasksByPost, setTasksByPost] = React.useState({});
  const [startedAt, setStartedAt] = React.useState(null);
  const [exhausted, setExhausted] = React.useState(false);
  const [refreshFailed, setRefreshFailed] = React.useState(false);
  const intervalRef = React.useRef(null);
  // "Continue the thread": { [taskId]: { busy, errorKey, done } }
  const [resumeByTask, setResumeByTask] = React.useState({});

  const stopPolling = React.useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchAll = React.useCallback(async (ids) => {
    const results = await Promise.allSettled(
      ids.map((id) => _request.postTasks.getByPost(id)),
    );
    const next = {};
    let failures = 0;
    results.forEach((r, idx) => {
      if (r.status === "fulfilled" && r.value?.isOk && Array.isArray(r.value.data)) {
        next[ids[idx]] = r.value.data;
      } else {
        failures += 1;
      }
    });
    // A poll that fetched nothing would otherwise leave the last snapshot on
    // screen indefinitely, which reads as "stuck" rather than "not refreshed".
    setRefreshFailed(failures > 0 && Object.keys(next).length === 0);
    setTasksByPost((prev) => ({ ...prev, ...next }));
  }, []);

  React.useEffect(() => {
    if (!open || !postIds || postIds.length === 0) {
      stopPolling();
      return undefined;
    }
    setTasksByPost({});
    setResumeByTask({});
    setExhausted(false);
    setRefreshFailed(false);
    setStartedAt(Date.now());
    fetchAll(postIds);
    intervalRef.current = setInterval(() => fetchAll(postIds), POLL_INTERVAL_MS);
    return stopPolling;
  }, [open, postIds?.join(","), fetchAll, stopPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  // Browsers throttle timers in a hidden tab to about once a minute, so a user
  // who switches away and comes back would stare at a snapshot minutes old —
  // exactly the "stuck on Queued" report. Catch up the moment we are visible.
  React.useEffect(() => {
    if (!open || !postIds || postIds.length === 0) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchAll(postIds);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [open, postIds?.join(","), fetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop polling once everything is terminal or we hit the cap.
  const allTasks = React.useMemo(
    () => Object.values(tasksByPost).flat(),
    [tasksByPost],
  );
  const summary = React.useMemo(() => summarizePublishTasks(allTasks), [allTasks]);
  const { allDone, anyFailed, succeeded, failed, progress } = summary;
  React.useEffect(() => {
    if (!open) return;
    if (allDone) {
      stopPolling();
    } else if (startedAt && Date.now() - startedAt > MAX_POLL_DURATION_MS) {
      stopPolling();
      setExhausted(true);
    }
  }, [allDone, allTasks.length, open, startedAt, stopPolling]);

  // A token usually turns out to be dead here, while the task is being
  // published — not in the create response the page inspects. Report it up so
  // the reconnect prompt appears in that case too, once per account.
  const reportedTokenFailures = React.useRef(new Set());
  React.useEffect(() => {
    if (!open || !onTokenFailures) return;
    const fresh = findTokenFailures(allTasks).filter(
      (f) => !reportedTokenFailures.current.has(`${f.socialType}:${f.accountId}`),
    );
    if (fresh.length === 0) return;
    fresh.forEach((f) => reportedTokenFailures.current.add(`${f.socialType}:${f.accountId}`));
    onTokenFailures(fresh);
  }, [allTasks, open, onTokenFailures]);

  React.useEffect(() => {
    if (!open) reportedTokenFailures.current = new Set();
  }, [open]);

  const accountsById = React.useMemo(() => {
    const map = new Map();
    (accounts || []).forEach((a) => a && map.set(a.id, a));
    return map;
  }, [accounts]);

  // A resumed chain is queued again: show that at once and poll until the worker is done with it.
  const handleResume = React.useCallback(
    async (task) => {
      setResumeByTask((prev) => ({ ...prev, [task.id]: { busy: true } }));
      const resp = await _request.postTasks.resumeThread(task.id);
      const errorKey = describeResumeError(resp);
      if (errorKey) {
        setResumeByTask((prev) => ({ ...prev, [task.id]: { busy: false, errorKey } }));
        return;
      }
      setResumeByTask((prev) => ({ ...prev, [task.id]: { busy: false, done: true } }));
      setTasksByPost((prev) => markTaskQueued(prev, task.id));
      stopPolling();
      setExhausted(false);
      setStartedAt(Date.now());
      const ids = postIds || [];
      intervalRef.current = setInterval(() => fetchAll(ids), POLL_INTERVAL_MS);
      fetchAll(ids);
    },
    [postIds, fetchAll, stopPolling],
  );

  const totalTasks = summary.total;
  const doneTasks = summary.done;

  const title = () => {
    if (!allDone) return tr ? tr("publishStatus.title") : "Publishing your post…";
    // A chain that broke after part 1 is a failed task, yet part of it is live: that is not "failed".
    const somethingLive = allTasks.some((t) => (describeThread(t)?.published || 0) > 0);
    if (anyFailed && succeeded === 0 && !somethingLive) {
      return tr ? tr("publishStatus.titleFailed") : "Publication failed";
    }
    if (anyFailed) {
      return tr ? tr("publishStatus.titlePartial") : "Publication finished with errors";
    }
    return tr ? tr("publishStatus.titleDone") : "Publication finished";
  };

  return (
    <Dialog open={open} onClose={allDone ? onClose : undefined} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 600 }}>{title()}</DialogTitle>
      <DialogContent>
        {totalTasks === 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", py: 2 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: "13px", opacity: 0.75 }}>
              {tr ? tr("publishStatus.loadingTasks") : "Loading tasks…"}
            </Typography>
          </Box>
        )}
        {totalTasks > 0 && (
          <>
            <Box sx={{ mb: "12px" }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                color={anyFailed && allDone ? "error" : "primary"}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography
                sx={{ mt: "4px", fontSize: "11px", opacity: 0.6, textAlign: "right" }}>
                {/* Never let a run of failures read as "1 / 1 done" — say what
                    published and what did not. */}
                {anyFailed
                  ? tr
                    ? tr("publishStatus.counts", { succeeded, failed })
                    : `${succeeded} published · ${failed} failed`
                  : `${doneTasks} / ${totalTasks}`}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {allTasks.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  account={accountsById.get(task.account_id)}
                  tr={tr}
                  onReconnect={onReconnect}
                  onResume={handleResume}
                  resume={resumeByTask[task.id]}
                />
              ))}
            </Box>
          </>
        )}
        {refreshFailed && (
          <Typography sx={{ mt: "10px", fontSize: "12px", color: "#ed6c02" }}>
            {tr
              ? tr("publishStatus.refreshFailed")
              : "Could not refresh the status — the post may already be published."}
          </Typography>
        )}
        {exhausted && (
          <Typography sx={{ mt: "10px", fontSize: "12px", opacity: 0.65, fontStyle: "italic" }}>
            {tr
              ? tr("publishStatus.timeout")
              : "Still running — you can check the status later on the dashboard."}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {!allDone && (
          <Button
            onClick={() => fetchAll(postIds || [])}
            startIcon={<RefreshIcon />}
            sx={{ textTransform: "none" }}>
            {tr ? tr("publishStatus.refresh") : "Refresh"}
          </Button>
        )}
        <Button onClick={onClose} variant={allDone ? "contained" : "text"}>
          {allDone
            ? tr
              ? tr("publishStatus.close")
              : "Close"
            : tr
            ? tr("publishStatus.runInBackground")
            : "Run in background"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishStatusDialog;
